import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ICallSheet, InitialPriceEstimateStatus, TopUpState } from "../../../../../models/CallQueue";
import { QuoteType } from "../../../../../models/Lead";
import PriceFormat from "../../../../../utils/priceFormat";
import { SectionCard } from "../Pill";

export interface BreakdownLookups {
  onExport: () => void;
  onCat: () => void;
  onValuation: () => void;
  onPartsEstimate: () => void;
  busy: "export" | "cat" | "valuation" | "estimate" | null;
}

function Line({ label, detail, value, strong }: { label: ReactNode; detail?: ReactNode; value: ReactNode; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className={strong ? "font-semibold text-green-800" : "text-gray-700"}>
        {label} {detail ? <span className="text-xs text-gray-600">{detail}</span> : null}
      </span>
      <span className={strong ? "font-bold tabular-nums text-green-800" : "font-semibold tabular-nums text-gray-900"}>{value}</span>
    </div>
  );
}

function LookupButton({ label, busy, onClick }: { label: string; busy: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} disabled={busy} className="text-xs font-medium text-primary-700 underline hover:text-primary-800 disabled:opacity-50">
      {busy ? "…" : label}
    </button>
  );
}

export default function MaxBreakdown({ sheet, lookups }: { sheet: ICallSheet; lookups: BreakdownLookups }) {
  const { t } = useTranslation("leads");
  const { offer } = sheet;
  const max = sheet.approvedPrice ?? offer.max;

  const topUp = (state: TopUpState, amount: number, onLookup: () => void, busy: boolean) => {
    switch (state) {
      case TopUpState.IncludedInQuote:
        return <span className="text-xs font-medium text-gray-600">{t("In the online quote")}</span>;
      case TopUpState.NeedsLookup:
        return <LookupButton label={t("Get price")} busy={busy} onClick={onLookup} />;
      case TopUpState.NoOffer:
        return <span className="text-xs font-medium text-gray-600">{t("No offer")}</span>;
      default:
        return PriceFormat(amount);
    }
  };

  const floorDetail = sheet.pricePerTonne
    ? `(${PriceFormat(sheet.pricePerTonne)}/t × ${(sheet.kerbWeightKg / 1000).toLocaleString("en-GB")} t)`
    : undefined;

  const estimateRunning =
    sheet.partsEstimateStatus === InitialPriceEstimateStatus.Queued || sheet.partsEstimateStatus === InitialPriceEstimateStatus.Running;

  return (
    <SectionCard
      title={t("How the max is built")}
      right={sheet.quoteType === QuoteType.ScrapExport && offer.exportTopUpState !== TopUpState.IncludedInQuote
        ? <LookupButton label={t("Refresh export")} busy={lookups.busy === "export"} onClick={lookups.onExport} />
        : undefined}
      className="flex-1 basis-72"
    >
      <div className="flex flex-col gap-2 px-4 pb-3.5 pt-3 text-[13px]">
        {sheet.quoteType === QuoteType.Salvage ? (
          <>
            <Line
              label={t("Salvage valuation")}
              value={sheet.salvageValuation
                ? PriceFormat(sheet.salvageValuation)
                : <LookupButton label={t("Get valuation")} busy={lookups.busy === "valuation"} onClick={lookups.onValuation} />}
            />
            <Line label={t("Floor")} value={offer.floor !== null && offer.floor !== undefined ? PriceFormat(offer.floor) : "—"} />
          </>
        ) : sheet.quoteType === QuoteType.SmartPrice ? (
          <>
            <Line label={t("Scrap value (floor)")} detail={floorDetail} value={offer.floor !== null && offer.floor !== undefined ? PriceFormat(offer.floor) : "—"} />
            <Line
              label={t("Initial price estimate")}
              value={sheet.partsEstimate
                ? PriceFormat(sheet.partsEstimate)
                : estimateRunning
                  ? <span className="text-xs text-gray-600">{t("Running…")}</span>
                  : <LookupButton label={t("Run estimate")} busy={lookups.busy === "estimate"} onClick={lookups.onPartsEstimate} />}
            />
          </>
        ) : (
          <>
            <Line label={t("Scrap value (floor)")} detail={floorDetail} value={offer.floor !== null && offer.floor !== undefined ? PriceFormat(offer.floor) : "—"} />
            <Line label={t("Online quote")} value={PriceFormat(sheet.onlineQuote)} />
            <Line
              label={`+ ${t("Export top-up")}`}
              detail={offer.exportValue ? `(${t("engine offer")} ${PriceFormat(offer.exportValue)})` : undefined}
              value={topUp(offer.exportTopUpState, offer.exportTopUp, lookups.onExport, lookups.busy === "export")}
            />
            <Line
              label={`+ ${t("Cat top-up")}`}
              detail={offer.catValue ? `(${t("cat value")} ${PriceFormat(offer.catValue)})` : undefined}
              value={topUp(offer.catTopUpState, offer.catTopUp, lookups.onCat, lookups.busy === "cat")}
            />
          </>
        )}

        <div className="border-t border-gray-200 pt-2">
          <Line label={`= ${sheet.approvedPrice ? t("Approved up to") : t("Max you can offer")}`} value={PriceFormat(max)} strong />
        </div>
        {offer.floor === null || offer.floor === undefined ? (
          <p className="text-xs text-slate-600">{t("No price breakdown for this lead.")}</p>
        ) : null}

        <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-600">
          <span>
            {t("Parts estimate")}
            <br />
            <strong className="text-[13px] text-slate-800">{sheet.partsEstimate ? PriceFormat(sheet.partsEstimate) : "—"}</strong>
          </span>
          <span>
            {t("Salvage valuation")}
            <br />
            <strong className="text-[13px] text-slate-800">{sheet.salvageValuation ? PriceFormat(sheet.salvageValuation) : "—"}</strong>
          </span>
          <span className="col-span-2">{t("Reference values only; they are not offerable.")}</span>
        </div>
      </div>
    </SectionCard>
  );
}
